import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { END_POINT, METHODS, BASE_URL_IMAGE } from "../../../helper/helper";
import { fetchData, ImageUpload } from "../../../helper/ApiCall";
import TopBar from "../sidebar/TopBar";
import { useSelector } from "react-redux";

const c = {
  bg: "#F4F6FD", surface: "#FFFFFF", border: "#E8ECF4",
  text: "#1A202C", textMuted: "#718096", textFaint: "#A0AEC0",
  accent: "#4F46E5", accentLight: "#EEF2FF", accentBorder: "#C7D2FE",
  danger: "#DC2626", dangerLight: "#FEE2E2", dangerBorder: "#FECACA",
};

const inp = (focused) => ({
  width: "100%", padding: "11px 14px 11px 40px", fontSize: "14px",
  border: "1.5px solid " + (focused ? c.accent : c.border),
  borderRadius: "10px", outline: "none",
  background: focused ? "#FAFBFF" : c.bg,
  color: c.text, fontFamily: "inherit", transition: "all 0.2s",
  boxShadow: focused ? "0 0 0 3px " + c.accentLight : "none",
  boxSizing: "border-box",
});

const Field = ({ label, icon, name, type = "text", placeholder, value, onChange, focused, setFocused }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
    <label style={{ fontSize: "11px", fontWeight: 700, color: c.textMuted, letterSpacing: "0.07em", textTransform: "uppercase" }}>{label}</label>
    <div style={{ position: "relative" }}>
      <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", fontSize: "15px", pointerEvents: "none" }}>{icon}</span>
      <input
        type={type} name={name} placeholder={placeholder} value={value} onChange={onChange}
        onFocus={() => setFocused(name)} onBlur={() => setFocused("")}
        style={inp(focused === name)}
      />
    </div>
  </div>
);

const UserProfile = () => {
  const navigate = useNavigate();
  const { userData } = useSelector((store) => store.userReducer);
  const cityid = userData._id;
  const fileRef = useRef(null);

  const [formData, setFormData] = useState({
    client_id: "", name: "", company_name: "", primary_phone: "",
    secondary_phone: "", address: "", contact_person: "", lead: "",
    location: "", amount: "", date: "", email: "", profile_pic: "",
  });
  const [profilePreview, setProfilePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [focused, setFocused] = useState("");
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => { if (cityid) cityHandler(); }, []);

  const cityHandler = async () => {
    try {
      const result = await fetchData(END_POINT.GetClientById + cityid);
      if (result.success) {
        setFormData({
          client_id: result.data.client_id, name: result.data.name,
          company_name: result.data.company_name, primary_phone: result.data.primary_phone,
          secondary_phone: result.data.secondary_phone, contact_person: result.data.contact_person,
          address: result.data.address, lead: result.data.lead,
          location: result.data.location, amount: result.data.amount,
          email: result.data.email, status: result.data.status,
          date: result.data.date, profile_pic: result.data.profile_pic || "",
        });
        if (result.data.profile_pic) setProfilePreview(`${BASE_URL_IMAGE}${result.data.profile_pic}`);
      }
    } catch (err) { console.log(err); }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const processFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setUploading(true);
    try {
      const uploadResult = await ImageUpload(file);
      if (uploadResult?.success && uploadResult.image) {
        setFormData((p) => ({ ...p, profile_pic: uploadResult.image }));
        setProfilePreview(`${BASE_URL_IMAGE}${uploadResult.image}`);
        Swal.fire({ icon: "success", title: "Uploaded!", timer: 1200, showConfirmButton: false });
      } else {
        Swal.fire("Upload failed", "Could not upload image.", "error");
      }
    } catch { Swal.fire("Upload failed", "Could not upload image.", "error"); }
    finally { setUploading(false); }
  };

  const handleImageChange = (e) => processFile(e.target.files?.[0]);
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); processFile(e.dataTransfer.files?.[0]); };
  const handleRemoveImage = () => { setFormData((p) => ({ ...p, profile_pic: "" })); setProfilePreview(null); };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const endpoint = cityid ? END_POINT.UpdateClient + cityid : END_POINT.UploadClient;
      const result = await fetchData(endpoint, METHODS.POST, JSON.stringify(formData));
      if (result.success) {
        Swal.fire({ title: "Saved!", text: "Profile has been updated.", icon: "success", confirmButtonColor: c.accent, confirmButtonText: "Great" })
          .then((r) => { if (r.isConfirmed) navigate("../client-profile"); });
      }
    } catch (err) { console.log(err); }
    finally { setSaving(false); }
  };

  const initials = (formData.name || userData?.name || "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const hasPhoto = !!profilePreview;

  return (
    <div style={{ minHeight: "100vh", background: c.bg, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes float { 0%,100%{ transform:translateY(0); } 50%{ transform:translateY(-5px); } }
        @keyframes pulse { 0%,100%{ opacity:1; } 50%{ opacity:.35; } }
        input::placeholder, textarea::placeholder { color: #A0AEC0; }
        ::-webkit-scrollbar { width:5px; }
        ::-webkit-scrollbar-thumb { background:#CBD5E0; border-radius:10px; }
      `}</style>

      <TopBar />

      <div style={{ maxWidth: "820px", margin: "0 auto", padding: "32px 24px 60px" }}>

        {/* ── PAGE HEADER ── */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "28px", animation: "fadeUp 0.3s ease both" }}>
          <button onClick={() => navigate(-1)} style={{ background: c.surface, border: "1px solid " + c.border, borderRadius: "10px", width: 36, height: 36, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", color: c.textMuted, flexShrink: 0, transition: "all 0.15s", fontFamily: "inherit" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = c.accentLight; e.currentTarget.style.color = c.accent; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = c.surface; e.currentTarget.style.color = c.textMuted; }}>←</button>
          <div style={{ width: 1, height: 24, background: c.border }} />
          <div style={{ width: 38, height: 38, borderRadius: "11px", background: "linear-gradient(135deg,#4F46E5,#7C3AED)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", boxShadow: "0 4px 12px rgba(79,70,229,0.3)" }}>⚙️</div>
          <div>
            <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 800, color: c.text, letterSpacing: "-0.3px" }}>Account Settings</h1>
            <p style={{ margin: 0, fontSize: "12px", color: c.textMuted }}>Manage your profile information</p>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>

          {/* ── PROFILE PICTURE CARD ── */}
          <div style={{ background: c.surface, border: "1px solid " + c.border, borderRadius: "20px", padding: "28px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", animation: "fadeUp 0.4s ease both" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: c.textFaint, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: "20px", display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ width: 3, height: 14, background: c.accent, borderRadius: "2px", display: "inline-block" }} />
              Profile Picture
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "24px", flexWrap: "wrap" }}>
              {/* Avatar with drag & drop */}
              <div
                style={{ position: "relative", flexShrink: 0 }}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
              >
                <div style={{
                  width: 90, height: 90, borderRadius: "22px",
                  border: dragOver ? "2.5px dashed " + c.accent : "2.5px solid " + c.accentBorder,
                  overflow: "hidden", background: c.accentLight,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 6px 20px rgba(79,70,229,0.15)",
                  transition: "all 0.2s",
                  animation: "float 4s ease-in-out infinite",
                  cursor: "pointer",
                }} onClick={() => fileRef.current?.click()}>
                  {hasPhoto ? (
                    <img src={profilePreview} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <span style={{ fontSize: "30px", fontWeight: 800, color: c.accent, userSelect: "none" }}>{initials}</span>
                  )}
                  {uploading && (
                    <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.88)", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "22px" }}>
                      <div style={{ width: 22, height: 22, border: "2.5px solid " + c.accentBorder, borderTopColor: c.accent, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                    </div>
                  )}
                </div>
                {/* Online dot */}
                <div style={{ position: "absolute", bottom: 4, right: 4, width: 13, height: 13, borderRadius: "50%", background: "#10B981", border: "2px solid #fff", boxShadow: "0 0 6px #10B981", animation: "pulse 2.5s infinite" }} />
              </div>

              {/* Info + controls */}
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: "15px", fontWeight: 700, color: c.text, marginBottom: "3px" }}>{formData.name || "Your Name"}</div>
                <div style={{ fontSize: "12px", color: c.textMuted, marginBottom: "14px" }}>Click the avatar or drag & drop · JPEG or PNG · Max 5MB</div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <label style={{
                    padding: "8px 16px", borderRadius: "9px", background: c.accentLight,
                    border: "1.5px solid " + c.accentBorder, color: c.accent, fontSize: "13px", fontWeight: 600,
                    cursor: uploading ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: "6px", transition: "all 0.15s",
                  }}
                    onMouseEnter={(e) => { if (!uploading) e.currentTarget.style.background = "#E0E7FF"; }}
                    onMouseLeave={(e) => e.currentTarget.style.background = c.accentLight}
                  >
                    {uploading ? (
                      <><div style={{ width: 12, height: 12, border: "2px solid " + c.accentBorder, borderTopColor: c.accent, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} /> Uploading…</>
                    ) : (<>📤 Change Photo</>)}
                    <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: "none" }} disabled={uploading} ref={fileRef} />
                  </label>
                  {hasPhoto && (
                    <button onClick={handleRemoveImage} style={{ padding: "8px 14px", borderRadius: "9px", background: c.dangerLight, border: "1.5px solid " + c.dangerBorder, color: c.danger, fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#FECACA"}
                      onMouseLeave={(e) => e.currentTarget.style.background = c.dangerLight}
                    >🗑️ Remove</button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── PERSONAL INFO CARD ── */}
          <div style={{ background: c.surface, border: "1px solid " + c.border, borderRadius: "20px", padding: "28px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", animation: "fadeUp 0.45s ease both", animationDelay: "0.06s" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: c.textFaint, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: "20px", display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ width: 3, height: 14, background: "#10B981", borderRadius: "2px", display: "inline-block" }} />
              Personal Information
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <Field label="Full Name"    icon="👤" name="name"          type="text"  placeholder="Enter your name"       value={formData.name}          onChange={handleChange} focused={focused} setFocused={setFocused} />
              <Field label="Email"        icon="✉️" name="email"         type="email" placeholder="Enter your email"      value={formData.email}         onChange={handleChange} focused={focused} setFocused={setFocused} />
              <Field label="Phone Number" icon="📞" name="primary_phone" type="text"  placeholder="Enter phone number"    value={formData.primary_phone} onChange={handleChange} focused={focused} setFocused={setFocused} />
              <Field label="Company"      icon="🏢" name="company_name"  type="text"  placeholder="Enter company name"    value={formData.company_name}  onChange={handleChange} focused={focused} setFocused={setFocused} />

              {/* Address — full width textarea */}
              <div style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: "6px" }}>
                <label style={{ fontSize: "11px", fontWeight: 700, color: c.textMuted, letterSpacing: "0.07em", textTransform: "uppercase" }}>Address</label>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: "12px", top: "12px", fontSize: "15px", pointerEvents: "none" }}>📍</span>
                  <textarea name="address" placeholder="Enter your address" value={formData.address} onChange={handleChange} rows={2}
                    onFocus={() => setFocused("address")} onBlur={() => setFocused("")}
                    style={{ ...inp(focused === "address"), paddingLeft: "40px", resize: "vertical", minHeight: "64px" }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ── ACTION BUTTONS ── */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", animation: "fadeUp 0.5s ease both", animationDelay: "0.1s" }}>
            <button type="button" onClick={cityHandler}
              style={{ padding: "11px 20px", borderRadius: "10px", border: "1.5px solid " + c.border, background: c.surface, color: c.textMuted, fontSize: "14px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s", display: "flex", alignItems: "center", gap: "6px" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = c.bg; e.currentTarget.style.borderColor = c.accentBorder; e.currentTarget.style.color = c.text; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = c.surface; e.currentTarget.style.borderColor = c.border; e.currentTarget.style.color = c.textMuted; }}
            >↺ Restore</button>

            <button type="button" onClick={handleSubmit} disabled={saving}
              style={{
                padding: "11px 28px", borderRadius: "10px", border: "none",
                background: saving ? c.accentBorder : "linear-gradient(135deg, #4F46E5, #7C3AED)",
                color: "#fff", fontSize: "14px", fontWeight: 700, cursor: saving ? "not-allowed" : "pointer",
                fontFamily: "inherit", transition: "all 0.2s",
                boxShadow: saving ? "none" : "0 4px 14px rgba(79,70,229,0.35)",
                display: "inline-flex", alignItems: "center", gap: "8px",
              }}
              onMouseEnter={(e) => { if (!saving) e.currentTarget.style.boxShadow = "0 6px 20px rgba(79,70,229,0.45)"; }}
              onMouseLeave={(e) => { if (!saving) e.currentTarget.style.boxShadow = "0 4px 14px rgba(79,70,229,0.35)"; }}
            >
              {saving ? (
                <><div style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />Saving…</>
              ) : "💾 Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;